// src/components/TermsOfService.js
import React from "react";
import { Container, Box, Typography, Paper, Button } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import PolicyContactSection from "./PolicyContactSection";

const TermsOfService = ({ onBack, primaryBlue, glassStyles }) => {
  const defaultPrimaryBlue = primaryBlue || {
    50: "#F0F8FF",
    100: "#E6F0FA",
    500: "#1E40AF",
    600: "#002366",
    700: "#001A4D",
    800: "#0B1C48",
  };

  const defaultGlassStyles = glassStyles || {
    background: "rgba(255, 255, 255, 0.25)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(255, 255, 255, 0.18)",
    borderRadius: "16px",
    boxShadow: "0 8px 32px rgba(11, 28, 72, 0.37)",
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${defaultPrimaryBlue[50]} 0%, ${defaultPrimaryBlue[100]} 100%)`,
        py: 4,
      }}
    >
      <Container maxWidth="md">
        <Button
          startIcon={<ArrowBack />}
          onClick={onBack}
          sx={{
            mb: 3,
            color: defaultPrimaryBlue[700],
            fontWeight: 600,
          }}
        >
          Back
        </Button>

        <Paper
          elevation={0}
          sx={{
            ...defaultGlassStyles,
            p: { xs: 3, md: 5 },
            bgcolor: "rgba(255, 255, 255, 0.9)",
          }}
        >
          <Typography
            variant="h3"
            gutterBottom
            sx={{
              fontWeight: 700,
              color: defaultPrimaryBlue[700],
              mb: 2,
            }}
          >
            Terms of Service
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Last Updated: January 2025
          </Typography>

          <Box sx={{ "& > *": { mb: 3 } }}>
            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                1. Agreement to Terms
              </Typography>
              <Typography variant="body1" paragraph>
                Welcome to InfinityWhale. By accessing or using our website and services, you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our services.
              </Typography>
              <Typography variant="body1" paragraph>
                These Terms constitute a legally binding agreement between you and InfinityWhale. We reserve the right to modify these Terms at any time, and your continued use of our services constitutes acceptance of any changes.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                2. Eligibility
              </Typography>
              <Typography variant="body1" paragraph>
                To use our services, you must:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • Be at least 18 years of age<br />
                • Have the legal capacity to enter into binding contracts<br />
                • Provide accurate and complete registration information<br />
                • Maintain the security of your account credentials<br />
                • Be a resident of India (for domestic orders)
              </Typography>
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                By creating an account, you represent and warrant that you meet these eligibility requirements.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                3. Account Registration and Security
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>3.1 Account Creation:</strong> You must create an account to access certain features. You agree to provide accurate, current, and complete information during registration.
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>3.2 Account Security:</strong> You are responsible for maintaining the confidentiality of your account credentials. You agree to:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • Use a strong, unique password<br />
                • Not share your account with others<br />
                • Notify us immediately of any unauthorized access<br />
                • Accept responsibility for all activities under your account
              </Typography>
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                <strong>3.3 Account Termination:</strong> We reserve the right to suspend or terminate your account if you violate these Terms or engage in fraudulent activity.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                4. Products and Services
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>4.1 Product Descriptions:</strong> We strive to provide accurate product descriptions, images, and pricing. However, we do not warrant that product descriptions or other content is accurate, complete, or error-free.
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>4.2 Pricing:</strong> All prices are listed in Indian Rupees (INR) and include applicable GST unless otherwise stated. We reserve the right to change prices at any time without notice.
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>4.3 Product Availability:</strong> Product availability is subject to change. We reserve the right to limit quantities or discontinue products at any time.
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>4.4 Packages and Subscriptions:</strong> Certain products are sold as packages or subscriptions with specific terms, benefits, and validity periods as described at the time of purchase.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                5. Orders and Payment
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>5.1 Order Placement:</strong> By placing an order, you make an offer to purchase products. We reserve the right to accept or reject any order for any reason.
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>5.2 Order Confirmation:</strong> After placing an order, you will receive an email confirmation. This does not constitute acceptance of your order. Acceptance occurs when we ship the product or confirm payment.
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>5.3 Payment Methods:</strong> We accept the following payment methods:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • UPI (Unified Payments Interface)<br />
                • Credit/Debit Cards<br />
                • Net Banking<br />
                • Digital Wallets<br />
                • Bank Transfer
              </Typography>
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                <strong>5.4 Payment Processing:</strong> All payments are processed securely through certified payment gateways. We do not store your complete payment card information.
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>5.5 Payment Verification:</strong> Orders are subject to payment verification. We may contact you to verify payment details before processing your order.
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>5.6 GST and Taxes:</strong> All applicable taxes, including GST, are included in the final price unless otherwise stated. GST invoices will be provided for all orders.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                6. Shipping and Delivery
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>6.1 Delivery Timeline:</strong> Standard delivery takes 7-10 business days from order confirmation. Delivery times may vary based on location and product availability.
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>6.2 Shipping Charges:</strong> Shipping charges are calculated based on order value:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • Orders above ₹2,999: FREE shipping<br />
                • Orders below ₹2,999: Shipping charges apply as per location
              </Typography>
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                <strong>6.3 Delivery Address:</strong> You must provide a complete and accurate delivery address. We are not responsible for delays or non-delivery due to incorrect addresses.
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>6.4 Risk of Loss:</strong> Risk of loss and title for products pass to you upon delivery to the shipping carrier.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                7. Referral Program
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>7.1 Program Overview:</strong> Our referral program allows users to earn rewards by referring new customers. Participation is subject to program terms.
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>7.2 Eligibility:</strong> To participate in the referral program, you must:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • Have an active account<br />
                • Have purchased at least one package (Ultra Customer status)<br />
                • Comply with program rules and guidelines
              </Typography>
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                <strong>7.3 Referral Rewards:</strong> Rewards are credited according to program terms and are subject to verification. We reserve the right to modify or terminate the program at any time.
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>7.4 Prohibited Activities:</strong> You may not:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • Create fake accounts or referrals<br />
                • Use spam or unsolicited communications<br />
                • Misrepresent the program or our products<br />
                • Engage in fraudulent activities
              </Typography>
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                Violation of these rules may result in forfeiture of rewards and account termination.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                8. Intellectual Property Rights
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>8.1 Ownership:</strong> All content on our website, including text, graphics, logos, images, software, and design, is the property of InfinityWhale and is protected by Indian and international copyright, trademark, and other intellectual property laws.
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>8.2 Limited License:</strong> We grant you a limited, non-exclusive, non-transferable license to access and use our website for personal, non-commercial purposes.
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>8.3 Restrictions:</strong> You may not:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • Copy, modify, or distribute our content without permission<br />
                • Use our trademarks or branding without authorization<br />
                • Reverse engineer or decompile our software<br />
                • Create derivative works based on our content
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                9. User Conduct
              </Typography>
              <Typography variant="body1" paragraph>
                You agree not to:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • Violate any applicable laws or regulations<br />
                • Infringe on the rights of others<br />
                • Transmit harmful code, viruses, or malware<br />
                • Interfere with the operation of our website<br />
                • Attempt unauthorized access to our systems<br />
                • Harass, abuse, or harm other users<br />
                • Use our services for fraudulent purposes<br />
                • Scrape or collect data without permission
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                10. Disclaimers and Limitation of Liability
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>10.1 Disclaimer of Warranties:</strong> Our services are provided "as is" and "as available" without warranties of any kind, either express or implied. We do not warrant that our services will be uninterrupted, error-free, or secure.
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>10.2 Limitation of Liability:</strong> To the maximum extent permitted by law, InfinityWhale shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of our services.
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>10.3 Maximum Liability:</strong> Our total liability for any claim arising from these Terms or your use of our services shall not exceed the amount you paid for the product or service giving rise to the claim.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                11. Indemnification
              </Typography>
              <Typography variant="body1" paragraph>
                You agree to indemnify, defend, and hold harmless InfinityWhale, its officers, directors, employees, and agents from any claims, liabilities, damages, losses, and expenses arising from:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • Your violation of these Terms<br />
                • Your violation of any rights of another party<br />
                • Your use of our services<br />
                • Your breach of any applicable laws
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                12. Dispute Resolution
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>12.1 Governing Law:</strong> These Terms are governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts in Bengaluru, Karnataka.
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>12.2 Informal Resolution:</strong> Before filing a legal claim, you agree to contact us at support@infinitywhale.com to attempt to resolve the dispute informally.
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>12.3 Arbitration:</strong> If informal resolution fails, disputes may be resolved through binding arbitration in accordance with the Arbitration and Conciliation Act, 1996.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                13. Termination
              </Typography>
              <Typography variant="body1" paragraph>
                We may terminate or suspend your account and access to our services immediately, without prior notice, for any reason, including:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • Violation of these Terms<br />
                • Fraudulent or illegal activity<br />
                • Extended period of inactivity<br />
                • At our sole discretion
              </Typography>
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                Upon termination, your right to use our services will immediately cease. Provisions that should survive termination will remain in effect.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                14. Modifications to Terms
              </Typography>
              <Typography variant="body1" paragraph>
                We reserve the right to modify these Terms at any time. We will notify you of significant changes by posting the updated Terms on our website and updating the "Last Updated" date. Your continued use of our services after changes constitutes acceptance of the modified Terms.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                15. Severability
              </Typography>
              <Typography variant="body1" paragraph>
                If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions will continue in full force and effect.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                16. Entire Agreement
              </Typography>
              <Typography variant="body1" paragraph>
                These Terms, together with our Privacy Policy and other policies, constitute the entire agreement between you and InfinityWhale regarding your use of our services.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                17. Contact Information
              </Typography>
              <PolicyContactSection intro="For questions about these Terms, please contact us:" />
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default TermsOfService;
